import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AIStrategyBuilderService from '~/api-services/AIStrategyBuilderService';
import {
    CreateStrategyRequest,
    StrategyCombination,
    StrategyCombinationConnection,
    StrategyCombinations,
    StrategyIndicatorInstance,
    StrategyInputNode,
    StrategyInstrumentInfo,
    StrategyNodeAttributeTemplate,
    StrategyNodeTemplate,
    StrategyOptimisation,
    StrategyOptimisations,
    OptimizationTypeOption,
    StrategyTemplate,
} from '~/api-services/AIStrategyBuilderService/types';
import ExchangeService from '~/api-services/ExchangeService';
import { ExchangeRates } from '~/api-services/ExchangeService/types';
import {
    DEFAULT_STRATEGY_INSTRUMENT,
    periodLabel,
    STRATEGY_PERIODS,
    StrategyPeriod,
} from '~/constants/strategyConstants';
import { useAppSelector } from '~/hooks';
import i18n from '~/i18n';
import { accountDetailsSelector, symbolsSelector } from '~/state/selectors';

import { defaultDistance as computeDefaultDistance, seedQuantity } from '../utils/defaults';
import { buildInstrumentOptions } from '../utils/instruments';
import { buildStrategyPayload, connectionField } from '../utils/payload';
import {
    optimisationStatesFromCloneNodes,
    rulesFromCloneNodes,
    rulesFromTemplate,
    seedNodeFromInstances,
} from '../utils/seeding';
import { containsAll, DEFAULT_MAX_RULES, emptyRule, OptimisationState, RuleState, sameCodes } from '../utils/types';
import {
    collectConstraintViolations,
    collectMissingFields,
    strategyCooldownSeconds,
    strategyTradingDisabledMessage,
    strategyValidationMessage,
} from '../utils/validation';

// Every step the wizard can show, in order. The steps actually rendered are a subset of these
// (`wizard.steps`) — the optimisation step is dropped for accounts without it — so components
// address steps by name (`goToStep`/`stepIndex`) rather than by a hardcoded index.
export const WIZARD_STEPS = ['market', 'trade', 'rules', 'optimisation', 'review'] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

// Which wizard step a submit-time error belongs to, so the review screen can deep-link to it.
export type SubmitError = { message: string; step?: number };

type Args = {
    template?: StrategyTemplate;
    cloneNodes?: StrategyInputNode[];
    onCreated: (strategyId?: string) => void;
};

/**
 * All state and behavior for the create wizard (docs/ai-strategy/03-create-strategy-wizard.md):
 * one draft object across five internal steps, metadata loads, instrument-derived clamps and
 * smart defaults, template/clone seeding, validity + recap summaries per step, and submit with
 * rate-limit cooldown and error->step mapping. The rule/payload/validation mechanics are the
 * Phase-0 extracted utils; the effects are ported from the PoC wizard.
 */
export function useCreateStrategyWizard({ template, cloneNodes, onCreated }: Args) {
    // Guard against stale / malformed route params that still claim to be a template
    // but are missing the nested objects added in later refactors.
    const validTemplate: StrategyTemplate | undefined =
        template && template.instrument && template.cloud ? template : undefined;

    const [step, setStep] = useState(0);

    // ---- Account-driven wizard shape ----
    // Both fields arrive on the load payload and are absent on older backends; each falls back
    // to the behavior that shipped before it existed (5 rules, optimisation step shown).
    const accountDetails = useAppSelector(accountDetailsSelector);
    const maxRules = useMemo(() => {
        const configured = accountDetails?.strategyMaxRules;
        return typeof configured === 'number' && configured >= 1 ? Math.floor(configured) : DEFAULT_MAX_RULES;
    }, [accountDetails?.strategyMaxRules]);
    const optimisationsEnabled = accountDetails?.strategyOptimisationsEnabled !== false;
    // Opt-in (defaults off): shows the exact request payload on the review step.
    const rawEnabled = accountDetails?.strategyRawEnabled === true;

    const steps = useMemo<WizardStep[]>(
        () => WIZARD_STEPS.filter((name) => name !== 'optimisation' || optimisationsEnabled),
        [optimisationsEnabled]
    );
    const stepIndex = useCallback((name: WizardStep) => steps.indexOf(name), [steps]);
    const goToStep = useCallback(
        (name: WizardStep) => {
            const index = steps.indexOf(name);
            if (index >= 0) {
                setStep(index);
            }
        },
        [steps]
    );
    // Keep the cursor in range if the step list shrinks under a live account update.
    useEffect(() => {
        setStep((prev) => Math.min(prev, steps.length - 1));
    }, [steps.length]);

    // ---- Step 1: market ----
    const [instrument, setInstrument] = useState<string | null>(validTemplate?.instrument.code ?? null);
    const [period, setPeriod] = useState<StrategyPeriod>(
        (validTemplate?.instrument.period as StrategyPeriod) ?? STRATEGY_PERIODS[0]
    );
    const [side, setSideState] = useState<'buy' | 'sell'>(
        validTemplate ? (validTemplate.cloud.quantity >= 0 ? 'buy' : 'sell') : 'buy'
    );

    // ---- Step 2: trade setup ----
    // The fund is derived server-side (2026-07-27): it is not a field, not a summary line, not
    // sent on create, and doesn't cap the quantity — the instrument's own range does.
    const [instrumentInfo, setInstrumentInfo] = useState<StrategyInstrumentInfo | null>(null);
    // Instrument -> account-currency profit/loss coefficients for the chosen instrument, frozen
    // into the created strategy's frontend_json. The backend needs them to fill backend_json at
    // all (required funds, both currency totals, the projected return), and the results screens
    // use them to restate per-trade profit in account currency at the creation-time rate.
    const [exchangeRates, setExchangeRates] = useState<ExchangeRates | null>(null);
    const [quantityMagnitude, setQuantityMagnitude] = useState(
        validTemplate ? Math.abs(validTemplate.cloud.quantity) : 0
    );
    const [takeProfitDistance, setTakeProfitDistance] = useState(validTemplate?.cloud.takeProfit ?? 0);
    const [stopLossDistance, setStopLossDistance] = useState(validTemplate?.cloud.stopLoss ?? 0);

    // ---- Step 3: rules + metadata ----
    const [combinations, setCombinations] = useState<StrategyCombinations | null>(null);
    const [indicatorInstances, setIndicatorInstances] = useState<Record<string, StrategyIndicatorInstance>>({});
    const [nodeTemplates, setNodeTemplates] = useState<Record<string, StrategyNodeTemplate>>({});
    const [rules, setRules] = useState<RuleState[]>([emptyRule()]);

    // ---- Step 4: optimisation ----
    const [optimisations, setOptimisations] = useState<StrategyOptimisations | null>(null);
    const [optimizationTypes, setOptimizationTypes] = useState<OptimizationTypeOption[]>([]);
    const [optimizationType, setOptimizationType] = useState('clear_balance');
    const [optimisationStates, setOptimisationStates] = useState<Record<number, OptimisationState>>({});

    // ---- Step 5: review + submit ----
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<SubmitError | null>(null);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);

    const [metaError, setMetaError] = useState<string | null>(null);
    const [metaLoading, setMetaLoading] = useState(true);

    const dirtyRef = useRef(false);
    const markDirty = () => {
        dirtyRef.current = true;
    };

    const skipNextDefaultReseedRef = useRef<boolean>(!!validTemplate);

    const symbolsMap = useAppSelector(symbolsSelector);
    const dataSymbol = instrument ? symbolsMap[instrument] : undefined;
    // The selected instrument shows its short ticker ("Gold"), falling back to the long
    // descriptive name and then the raw code — the collapsed field and the recap line have no
    // room for "Euro vs. US Dollar", and the rest of the app names instruments this way too.
    const instrumentLabel = useCallback(
        (code: string | null) => {
            if (!code) {
                return '—';
            }
            const symbol = symbolsMap[code];
            return symbol?.nameTranslated || symbol?.descriptionTranslated || code;
        },
        [symbolsMap]
    );

    // The market picker offers every streamed symbol — the backend does not restrict
    // create/execute by instrument.
    const instrumentOptions = useMemo(() => buildInstrumentOptions(symbolsMap), [symbolsMap]);

    // ---- Metadata loads ----
    const loadMeta = useCallback(() => {
        setMetaLoading(true);
        setMetaError(null);
        Promise.all([
            AIStrategyBuilderService.getStrategyCombinations(),
            AIStrategyBuilderService.getStrategyIndicatorInstances(),
            AIStrategyBuilderService.getStrategyNodeTemplates(),
            AIStrategyBuilderService.getStrategyOptimisations(),
            AIStrategyBuilderService.getOptimizationTypes(),
        ])
            .then(([combinationsRes, instancesRes, nodesRes, optimisationsRes, optimizationTypesRes]) => {
                if (combinationsRes.data?.status && combinationsRes.data.returnData) {
                    setCombinations(combinationsRes.data.returnData);
                }
                if (instancesRes.data?.status && instancesRes.data.returnData) {
                    setIndicatorInstances(instancesRes.data.returnData);
                }
                if (nodesRes.data?.status && nodesRes.data.returnData) {
                    setNodeTemplates(nodesRes.data.returnData);
                }
                if (optimisationsRes.data?.status && optimisationsRes.data.returnData) {
                    setOptimisations(optimisationsRes.data.returnData);
                }
                if (optimizationTypesRes.data?.status && optimizationTypesRes.data.returnData) {
                    setOptimizationTypes(optimizationTypesRes.data.returnData);
                }
                setMetaLoading(false);
            })
            .catch(() => {
                setMetaError(i18n.t('ai-strategy-wizard-meta-failed'));
                setMetaLoading(false);
            });
    }, []);

    useEffect(() => {
        loadMeta();
    }, [loadMeta]);

    // Seed a blank draft with gold — one of the most traded instruments — once symbols have
    // streamed in, falling back to the first option when the brand does not offer it. Template
    // and clone drafts arrive with an instrument already set, so this never fires for them, and
    // the early return keeps it a no-op after the first seed — instrumentOptions recomputes on
    // every price tick.
    useEffect(() => {
        if (instrument !== null || instrumentOptions.length === 0) {
            return;
        }
        const preferred = instrumentOptions.find((option) => option.value === DEFAULT_STRATEGY_INSTRUMENT);
        setInstrument((preferred ?? instrumentOptions[0]).value);
    }, [instrument, instrumentOptions]);

    // ---- Clone / template seeding (once metadata is in) ----
    const cloneAppliedRef = useRef(false);
    useEffect(() => {
        if (cloneAppliedRef.current || !cloneNodes || cloneNodes.length === 0) {
            return;
        }
        if (!combinations || Object.keys(indicatorInstances).length === 0) {
            return;
        }
        // A cloned strategy can carry more rules than this account may now create; keep the
        // first `maxRules` rather than seeding a draft that can never be submitted.
        const builtRules = rulesFromCloneNodes(cloneNodes).slice(0, maxRules);
        if (builtRules.length > 0) {
            setRules(builtRules);
        }
        cloneAppliedRef.current = true;
    }, [cloneNodes, combinations, indicatorInstances, maxRules]);

    const cloneOptAppliedRef = useRef(false);
    useEffect(() => {
        if (cloneOptAppliedRef.current || !cloneNodes || cloneNodes.length === 0 || !optimisations) {
            return;
        }
        const sideOpts = optimisations[side] ?? [];
        if (sideOpts.length === 0) {
            return;
        }
        const states = optimisationStatesFromCloneNodes(cloneNodes, sideOpts);
        if (Object.keys(states).length > 0) {
            setOptimisationStates(states);
        }
        cloneOptAppliedRef.current = true;
    }, [cloneNodes, optimisations, side]);

    const templateAppliedRef = useRef(false);
    useEffect(() => {
        if (templateAppliedRef.current || cloneNodes?.length) {
            return;
        }
        if (!validTemplate?.rules?.length) {
            return;
        }
        if (!combinations || Object.keys(indicatorInstances).length === 0) {
            return;
        }
        const builtRules = rulesFromTemplate(validTemplate, indicatorInstances).slice(0, maxRules);
        if (builtRules.length > 0) {
            setRules(builtRules);
        }
        templateAppliedRef.current = true;
    }, [validTemplate, cloneNodes, combinations, indicatorInstances, maxRules]);

    // Switching side invalidates the rules (combinations are per direction).
    const setSide = useCallback((next: 'buy' | 'sell') => {
        setSideState((prev) => {
            if (prev !== next) {
                setRules([emptyRule()]);
                setOptimisationStates({});
            }
            return next;
        });
    }, []);

    // ---- Instrument info + smart defaults ----
    const defaultDistance = useCallback(
        (minVal: number, maxVal: number, decimals: number, kind: 'tp' | 'sl'): number => {
            if (!dataSymbol) {
                return minVal;
            }
            return computeDefaultDistance({
                bid: dataSymbol.price.bid,
                ask: dataSymbol.price.ask,
                side,
                kind,
                minVal,
                maxVal,
                decimals,
            });
        },
        [dataSymbol, side]
    );

    useEffect(() => {
        if (!instrument) {
            return;
        }
        // Stale-response guard: picking A (slow) then B (fast) must not let A's late
        // response install A's ranges/defaults against a draft that now says B.
        let stale = false;
        setInstrumentInfo(null);
        AIStrategyBuilderService.getStrategyInstrument(instrument)
            .then(({ data }) => {
                if (stale || !data?.status || !data.returnData) {
                    return;
                }
                const info = data.returnData;
                setInstrumentInfo(info);
                if (!skipNextDefaultReseedRef.current) {
                    setTakeProfitDistance(
                        defaultDistance(
                            Number(info.takeProfitDistance.minValue),
                            Number(info.takeProfitDistance.maxValue),
                            info.distancePrecision,
                            'tp'
                        )
                    );
                    setStopLossDistance(
                        defaultDistance(
                            Number(info.stopLossDistance.minValue),
                            Number(info.stopLossDistance.maxValue),
                            info.distancePrecision,
                            'sl'
                        )
                    );
                    const range = side === 'buy' ? info.buy : info.sell;
                    setQuantityMagnitude(seedQuantity(range));
                }
            })
            .catch(() => {
                // Step 2 stays gated until instrument info loads (stepValid below).
            });
        return () => {
            stale = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instrument]);

    // Best-effort: a failure here must not block creating a strategy — it only costs the
    // account-currency figures on the result screens.
    useEffect(() => {
        if (!instrument) {
            return;
        }
        let stale = false;
        setExchangeRates(null);
        ExchangeService.getProfitLossExchangeRates(instrument)
            .then(({ data }) => {
                if (!stale && data?.status && data.returnData) {
                    setExchangeRates(data.returnData);
                }
            })
            .catch(() => {
                // leave null — the payload simply omits exchangeRates
            });
        return () => {
            stale = true;
        };
    }, [instrument]);

    useEffect(() => {
        if (!instrumentInfo) {
            return;
        }
        if (skipNextDefaultReseedRef.current) {
            skipNextDefaultReseedRef.current = false;
            return;
        }
        // TP/SL are seeded once per instrument (above) and otherwise user-controlled; only the
        // quantity floor tracks side changes.
        const range = side === 'buy' ? instrumentInfo.buy : instrumentInfo.sell;
        setQuantityMagnitude(seedQuantity(range));
    }, [side, instrumentInfo]);

    // ---- Rule editing ----
    const updateRule = useCallback((i: number, patch: Partial<RuleState>) => {
        dirtyRef.current = true;
        setRules((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    }, []);
    const addRule = useCallback(() => {
        dirtyRef.current = true;
        setRules((prev) => (prev.length >= maxRules ? prev : [...prev, emptyRule()]));
    }, [maxRules]);
    const canAddRule = rules.length < maxRules;
    const removeRule = useCallback((i: number) => {
        dirtyRef.current = true;
        setRules((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));
    }, []);

    const sideCombinations = useMemo(() => (combinations ? combinations[side] : []), [combinations, side]);
    const sideOptimisations = useMemo<StrategyOptimisation[]>(
        () => (optimisations ? optimisations[side] : []),
        [optimisations, side]
    );

    const optimisationSupportsInstrument = useCallback(
        (opt: StrategyOptimisation) =>
            !opt.supportedInstruments?.length ||
            opt.supportedInstruments.includes('ALL') ||
            (!!instrument && opt.supportedInstruments.includes(instrument)),
        [instrument]
    );
    const supportedOptimisationCount = useMemo(
        () => sideOptimisations.filter(optimisationSupportsInstrument).length,
        [sideOptimisations, optimisationSupportsInstrument]
    );

    // With the step skipped there is no UI to toggle optimisations, but clone seeding can still
    // populate states from the source strategy — so drop them everywhere downstream (payload,
    // validation, recap) rather than silently submitting filters the user never saw.
    const activeOptimisationStates = useMemo<Record<number, OptimisationState>>(
        () => (optimisationsEnabled ? optimisationStates : {}),
        [optimisationsEnabled, optimisationStates]
    );

    const toggleOptimisation = useCallback(
        (index: number) => {
            dirtyRef.current = true;
            setOptimisationStates((prev) => {
                if (prev[index]) {
                    const next = { ...prev };
                    delete next[index];
                    return next;
                }
                const opt = sideOptimisations[index];
                const nodes: Record<string, Record<string, unknown>> = {};
                if (opt) {
                    for (const code of opt.indicatorInstances) {
                        const node = seedNodeFromInstances(indicatorInstances, code);
                        if (node) {
                            nodes[code] = node;
                        }
                    }
                }
                return { ...prev, [index]: { nodes, editOpen: {} } };
            });
        },
        [sideOptimisations, indicatorInstances]
    );

    const updateOptimisation = useCallback((index: number, patch: Partial<OptimisationState>) => {
        dirtyRef.current = true;
        setOptimisationStates((prev) => (prev[index] ? { ...prev, [index]: { ...prev[index], ...patch } } : prev));
    }, []);

    const instanceLabel = useCallback(
        (code: string) => indicatorInstances[code]?.shortDisplayName ?? code,
        [indicatorInstances]
    );

    // The indicator's spelled-out name for the picker's second line. Empty when it would only
    // repeat the short label, case-insensitively ("PRICE" / "Price").
    const instanceDescription = useCallback(
        (code: string) => {
            const instance = indicatorInstances[code];
            const description = instance?.displayName || instance?.fullName || '';
            return description.toLowerCase() === instanceLabel(code).toLowerCase() ? '' : description;
        },
        [indicatorInstances, instanceLabel]
    );

    // Indicators still selectable at picker `slot`: every code, at any position, of the combinations
    // that contain all the indicators already chosen, minus those already chosen. Position carries no
    // meaning, so slot 0 offers every indicator of the side. Backend order is preserved here to keep
    // the greedy auto-extend deterministic; the picker sorts for display.
    const optionsAtSlot = useCallback(
        (picked: string[], slot: number): string[] => {
            const chosen = picked.slice(0, slot);
            const seen = new Set<string>(chosen);
            const out: string[] = [];
            for (const c of sideCombinations) {
                if (!containsAll(chosen, c.indicatorInstances)) {
                    continue;
                }
                for (const code of c.indicatorInstances) {
                    if (!seen.has(code)) {
                        seen.add(code);
                        out.push(code);
                    }
                }
            }
            return out;
        },
        [sideCombinations]
    );

    // The combination holding exactly this set of indicators, in any order. Sets are unique across a
    // side's combinations, so at most one can match.
    const comboForCodes = useCallback(
        (codes: string[]): StrategyCombination | null =>
            (codes.length > 0 && sideCombinations.find((c) => sameCodes(c.indicatorInstances, codes))) || null,
        [sideCombinations]
    );

    // Editable param fields for an indicator (hide name/type — fixed by selection).
    const paramFields = useCallback(
        (code: string): StrategyNodeAttributeTemplate[] =>
            (indicatorInstances[code]?.fields ?? []).filter((f) => f.name !== 'name' && f.name !== 'type'),
        [indicatorInstances]
    );

    const seedNode = useCallback(
        (code: string): Record<string, unknown> | null => seedNodeFromInstances(indicatorInstances, code),
        [indicatorInstances]
    );

    // Read a connection field, falling back to the node-type template default.
    const connectionFieldOrDefault = useCallback(
        (conn: StrategyCombinationConnection, name: string): unknown => {
            const fromConn = conn.fields.find((f) => f.name === name);
            if (fromConn) {
                return fromConn.defaultValue;
            }
            return nodeTemplates[conn.type]?.fields?.find((f) => f.name === name)?.defaultValue;
        },
        [nodeTemplates]
    );

    // "CrossoverIF SMA short.sma crosses above SMA long.sma", with "(prev)" for lag-1 operands.
    const describeConnection = useCallback(
        (conn: StrategyCombinationConnection): string => {
            const mathSign = String(connectionField(conn, 'mathSign') ?? '');
            const verb = conn.type === 'CrossoverIF' ? `${i18n.t('crosses') ?? 'crosses'} ${mathSign}` : mathSign;
            const lagX = Number(connectionFieldOrDefault(conn, 'lagX') ?? 0);
            const lagY = Number(connectionFieldOrDefault(conn, 'lagY') ?? 0);
            const operand = (ref: { indicatorInstance: string; output: string }, lag: number) =>
                `${lag === 1 ? '(prev) ' : ''}${instanceLabel(ref.indicatorInstance)}.${ref.output}`;
            const shortName = nodeTemplates[conn.type]?.shortName ?? conn.type;
            return `${shortName} ${operand(conn.x, lagX)} ${verb} ${operand(conn.y, lagY)}`.replace(/\s+/g, ' ').trim();
        },
        [instanceLabel, nodeTemplates, connectionFieldOrDefault]
    );

    // ---- Validation ----
    const combinationFor = useCallback((rule: RuleState) => comboForCodes(rule.codes), [comboForCodes]);

    // Indicators of a rule for display. Pick order is arbitrary, so a resolved rule is listed in
    // combination order — the same order buildPayload writes into frontendJson.indicators.
    const ruleCodesForDisplay = useCallback(
        (rule: RuleState): string[] => combinationFor(rule)?.indicatorInstances ?? rule.codes,
        [combinationFor]
    );

    const violationsFor = useCallback(
        (rule: RuleState): string[] => {
            const out: string[] = [];
            for (const code of rule.codes) {
                const node = rule.nodes[code];
                const instance = indicatorInstances[code];
                if (node && instance) {
                    out.push(...collectConstraintViolations(instance.fields, instance.constraints, node));
                }
            }
            return out;
        },
        [indicatorInstances]
    );

    const ruleComplete = useCallback(
        (rule: RuleState) =>
            !!combinationFor(rule) && rule.codes.length > 0 && rule.codes.every((c) => !!rule.nodes[c]),
        [combinationFor]
    );
    const ruleValid = useCallback(
        (rule: RuleState) => ruleComplete(rule) && violationsFor(rule).length === 0,
        [ruleComplete, violationsFor]
    );
    const allRulesValid = useMemo(() => rules.length > 0 && rules.every(ruleValid), [rules, ruleValid]);

    const tradeValuesValid = useMemo(() => {
        if (!instrumentInfo) {
            return false;
        }
        // The instrument's own range is the only quantity gate now; if the backend's derived
        // fund can't cover the size, it reports that on create (mapped by strategyValidationMessage).
        const range = side === 'buy' ? instrumentInfo.buy : instrumentInfo.sell;
        const minMag = Math.abs(Number(range.minQuantity));
        const maxMag = Math.abs(Number(range.maxQuantity));
        const tpMin = Number(instrumentInfo.takeProfitDistance.minValue);
        const tpMax = Number(instrumentInfo.takeProfitDistance.maxValue);
        const slMin = Number(instrumentInfo.stopLossDistance.minValue);
        const slMax = Number(instrumentInfo.stopLossDistance.maxValue);
        return (
            Number.isFinite(quantityMagnitude) &&
            quantityMagnitude >= minMag &&
            quantityMagnitude <= maxMag &&
            takeProfitDistance >= tpMin &&
            takeProfitDistance <= tpMax &&
            stopLossDistance >= slMin &&
            stopLossDistance <= slMax
        );
    }, [instrumentInfo, side, quantityMagnitude, takeProfitDistance, stopLossDistance]);

    // Per-name so the array stays aligned with whichever steps are actually rendered.
    const stepValid: boolean[] = useMemo(() => {
        const byName: Record<WizardStep, boolean> = {
            market: !!instrument,
            trade: tradeValuesValid,
            rules: allRulesValid,
            optimisation: !!optimizationType,
            review: !!instrument && tradeValuesValid && allRulesValid && !!optimizationType,
        };
        return steps.map((name) => byName[name]);
    }, [steps, instrument, tradeValuesValid, allRulesValid, optimizationType]);

    // ---- Payload + submit ----
    const buildPayload = useCallback(
        (): CreateStrategyRequest | null =>
            buildStrategyPayload({
                instrument,
                period,
                side,
                quantityMagnitude,
                takeProfitDistance,
                stopLossDistance,
                exchangeRates,
                optimizationType,
                rules,
                allRulesValid,
                instrumentInfo,
                nodeTemplates,
                indicatorInstances,
                instanceLabel,
                optimisationStates: activeOptimisationStates,
                sideOptimisations,
                combinationFor,
                optimisationSupportsInstrument,
            }),
        [
            instrument,
            allRulesValid,
            instrumentInfo,
            period,
            side,
            quantityMagnitude,
            stopLossDistance,
            takeProfitDistance,
            exchangeRates,
            instanceLabel,
            rules,
            combinationFor,
            nodeTemplates,
            activeOptimisationStates,
            sideOptimisations,
            optimisationSupportsInstrument,
            indicatorInstances,
            optimizationType,
        ]
    );

    // Full pre-submit validation, mapping failures to the step that fixes them.
    const validateAll = useCallback((): SubmitError | null => {
        if (!instrument) {
            return { message: i18n.t('ai-strategy-wizard-need-instrument'), step: stepIndex('market') };
        }
        if (!instrumentInfo || !tradeValuesValid) {
            return { message: i18n.t('ai-strategy-wizard-fix-trade'), step: stepIndex('trade') };
        }
        if (rules.length === 0 || !allRulesValid) {
            return { message: i18n.t('at-least-one-rule-required'), step: stepIndex('rules') };
        }
        const missing: string[] = [];
        const violations: string[] = [];
        rules.forEach((rule) => {
            for (const code of rule.codes) {
                const node = rule.nodes[code];
                if (node) {
                    missing.push(...collectMissingFields(paramFields(code), node));
                }
            }
            violations.push(...violationsFor(rule));
        });
        if (missing.length > 0 || violations.length > 0) {
            return {
                message: [...missing.map((m) => `${i18n.t('required')}: ${m}`), ...violations].join('; '),
                step: stepIndex('rules'),
            };
        }
        for (const key of Object.keys(activeOptimisationStates)) {
            const state = activeOptimisationStates[Number(key)];
            const opt = sideOptimisations[Number(key)];
            if (!opt) {
                continue;
            }
            for (const code of opt.indicatorInstances) {
                const node = state.nodes[code];
                const instance = indicatorInstances[code];
                const optIssues: string[] = [];
                if (node) {
                    optIssues.push(...collectMissingFields(paramFields(code), node));
                }
                if (node && instance) {
                    optIssues.push(...collectConstraintViolations(instance.fields, instance.constraints, node));
                }
                if (optIssues.length > 0) {
                    return { message: `${opt.name}: ${optIssues.join('; ')}`, step: stepIndex('optimisation') };
                }
            }
        }
        return null;
    }, [
        instrument,
        instrumentInfo,
        tradeValuesValid,
        rules,
        allRulesValid,
        paramFields,
        violationsFor,
        activeOptimisationStates,
        sideOptimisations,
        indicatorInstances,
        stepIndex,
    ]);

    const submit = useCallback(async () => {
        const invalid = validateAll();
        if (invalid) {
            setSubmitError(invalid);
            return;
        }
        const payload = buildPayload();
        if (!payload) {
            setSubmitError({ message: i18n.t('ai-strategy-wizard-build-failed') });
            return;
        }
        setSubmitting(true);
        setSubmitError(null);
        const handleFailure = (data: unknown) => {
            const cooldown = strategyCooldownSeconds(data);
            if (cooldown !== null) {
                setCooldownSeconds(cooldown);
                return;
            }
            const tradingDisabled = strategyTradingDisabledMessage(data);
            if (tradingDisabled) {
                setSubmitError({ message: tradingDisabled });
                return;
            }
            const mapped = strategyValidationMessage(data);
            setSubmitError({
                message:
                    mapped ?? (data as { message?: string })?.message ?? i18n.t('ai-strategy-wizard-create-failed'),
                // The engine's structured validation errors are all trade-parameter bounds.
                step: mapped ? stepIndex('trade') : undefined,
            });
        };
        try {
            const { data } = await AIStrategyBuilderService.createStrategy(payload);
            if (data?.status) {
                dirtyRef.current = false;
                onCreated(data.returnData?.id);
            } else {
                handleFailure(data);
            }
        } catch (err) {
            handleFailure((err as { response?: { data?: unknown } })?.response?.data);
        } finally {
            setSubmitting(false);
        }
    }, [validateAll, buildPayload, onCreated, stepIndex]);

    // Tick the create cooldown down to 0 once per second.
    useEffect(() => {
        if (cooldownSeconds <= 0) {
            return;
        }
        const interval = setInterval(() => {
            setCooldownSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => clearInterval(interval);
    }, [cooldownSeconds]);

    // ---- Recap / review summaries ----
    const distanceDecimals = instrumentInfo?.distancePrecision ?? 0;
    const summaries = useMemo(() => {
        const sideLabel = side === 'buy' ? i18n.t('buy') : i18n.t('sell');
        const market = instrument ? `${instrumentLabel(instrument)} · ${periodLabel(period)} · ${sideLabel}` : '';
        const trade = instrumentInfo
            ? `${i18n.t('quantity')} ${quantityMagnitude.toFixed(
                  instrumentInfo.quantityPrecision
              )} · TP ${takeProfitDistance.toFixed(distanceDecimals)} · SL ${stopLossDistance.toFixed(
                  distanceDecimals
              )}`
            : '';
        // Named exactly like the rule cards in step 3 ("EMA short + EMA long") so the recap and
        // the review echo what the user picked; the conditions themselves stay on the cards.
        const ruleTitles = rules
            .filter(ruleValid)
            .map((r) => ruleCodesForDisplay(r).map(instanceLabel).join(' + '))
            .filter(Boolean)
            .join(' · ');
        const enabledOpts = Object.keys(activeOptimisationStates)
            .map((k) => sideOptimisations[Number(k)])
            .filter((o) => o && optimisationSupportsInstrument(o))
            .map((o) => o.name);
        const optimisationText = [
            optimizationTypes.find((o) => o.value === optimizationType)?.text ?? optimizationType,
            ...enabledOpts,
        ].join(' · ');
        return { market, trade, rules: ruleTitles, optimisationText };
    }, [
        side,
        instrument,
        instrumentLabel,
        period,
        instrumentInfo,
        quantityMagnitude,
        takeProfitDistance,
        stopLossDistance,
        distanceDecimals,
        rules,
        ruleValid,
        ruleCodesForDisplay,
        instanceLabel,
        activeOptimisationStates,
        sideOptimisations,
        optimisationSupportsInstrument,
        optimizationTypes,
        optimizationType,
    ]);

    return {
        // step control
        step,
        setStep,
        stepValid,
        steps,
        stepIndex,
        goToStep,
        // Read via a function: navigation listeners capture render-time snapshots, and the
        // ref-based reset after a successful create must be visible to them immediately.
        isDirty: () => dirtyRef.current,
        markDirty,
        // metadata
        metaLoading,
        metaError,
        retryMeta: loadMeta,
        // market
        instrument,
        setInstrument,
        instrumentOptions,
        instrumentLabel,
        period,
        setPeriod,
        side,
        setSide,
        // trade setup
        instrumentInfo,
        quantityMagnitude,
        setQuantityMagnitude,
        takeProfitDistance,
        setTakeProfitDistance,
        stopLossDistance,
        setStopLossDistance,
        distanceDecimals,
        // rules
        rules,
        updateRule,
        addRule,
        removeRule,
        maxRules,
        canAddRule,
        indicatorInstances,
        nodeTemplates,
        instanceLabel,
        instanceDescription,
        paramFields,
        seedNode,
        optionsAtSlot,
        comboForCodes,
        combinationFor,
        ruleCodesForDisplay,
        describeConnection,
        violationsFor,
        ruleValid,
        allRulesValid,
        // optimisation
        optimisationsEnabled,
        optimizationTypes,
        optimizationType,
        setOptimizationType,
        sideOptimisations,
        optimisationStates,
        toggleOptimisation,
        updateOptimisation,
        optimisationSupportsInstrument,
        supportedOptimisationCount,
        // review + submit
        rawEnabled,
        buildPayload,
        submit,
        submitting,
        submitError,
        setSubmitError,
        cooldownSeconds,
        // summaries
        summaries,
    };
}

export type CreateStrategyWizardApi = ReturnType<typeof useCreateStrategyWizard>;
